import { useProductsByCarrierQuery } from "@qw/graphql";
import {
  Button,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Spinner,
} from "@qw/ui";
import { type InputHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

interface UploadContractDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (file: File, appetiteProductIds: string[]) => void;
  carrierSlug: string;
  isUploading: boolean;
}

export function UploadContractDialog({
  open,
  onClose,
  onConfirm,
  carrierSlug,
  isUploading,
}: UploadContractDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  const { data: productsData, loading: productsLoading } = useProductsByCarrierQuery({
    variables: { carrierSlug },
    skip: !carrierSlug,
  });

  const products = useMemo(() => productsData?.productsByCarrier ?? [], [productsData?.productsByCarrier]);

  const hasSetDefaultProducts = useRef(false);
  useEffect(() => {
    if (!open) {
      hasSetDefaultProducts.current = false;
      setFile(null);
      setSelectedProductIds([]);
      return;
    }
    if (products.length > 0 && !hasSetDefaultProducts.current) {
      hasSetDefaultProducts.current = true;
      setSelectedProductIds(products.map((p) => p.id));
    }
  }, [open, products]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const dropped = acceptedFiles[0];
    if (dropped) {
      setFile(dropped);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
    onDragEnter: undefined,
    onDragLeave: undefined,
    onDragOver: undefined,
  });

  const toggleProduct = (productId: string) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const handleUpload = () => {
    if (!file) {
      return;
    }

    onConfirm(file, selectedProductIds);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isUploading) {
      setFile(null);
      setSelectedProductIds([]);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Contract</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div
            {...getRootProps()}
            className={`border rounded-lg p-6 text-center cursor-pointer transition-colors ${
              isDragActive
                ? "border-primary bg-primary/5"
                : file
                  ? "border-muted-foreground/30 bg-muted/30"
                  : "border-muted-foreground/30 hover:border-primary/50"
            }`}
          >
            <input {...(getInputProps() as InputHTMLAttributes<HTMLInputElement>)} />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <Icon icon="description" className="text-muted-foreground" />
                <span className="text-sm font-medium">{file.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                >
                  <Icon icon="close" />
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <Icon icon="cloud_upload" className="text-2xl" />
                <p className="text-sm">Drop a PDF here or click to select</p>
              </div>
            )}
          </div>

          {products.length > 0 && (
            <Collapsible defaultOpen={false}>
              <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors [&[data-state=open]_.icon]:rotate-90">
                <Icon icon="chevron_right" className="icon h-4 w-4 transition-transform" />
                Applied to {selectedProductIds.length} products:
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                {productsLoading ? (
                  <Spinner />
                ) : (
                  <div className="space-y-2">
                    {products.map((product) => (
                      <label
                        key={product.id}
                        className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded p-1 -mx-1"
                      >
                        <Checkbox
                          checked={selectedProductIds.includes(product.id)}
                          onCheckedChange={() => toggleProduct(product.id)}
                        />
                        {product.name}
                      </label>
                    ))}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button type="button" onClick={handleUpload} disabled={!file || isUploading}>
            {isUploading ? <Spinner className="mr-2" /> : null}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
